import { slicedToArray as _slicedToArray } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useState } from 'react';
import { Spacer, Paragraph, FormHelp, TextButton } from '@zillow/constellation';
import { yieldCallback } from '@zillow/yield-callback';
import PropTypes from 'prop-types';
import TextField from '../Fields/TextField.js';
import { FieldConfig } from './DTICalculator.utils.js';
import { commaSeparateNumber } from '../../utils/zhl-calculator.js';
import { trackLabel } from './trackLabel.js';
import { handleFieldChangeEvent } from './DTICalculator.clickstream.js';
import { MODAL_DIALOG, DTI_CALC, TOOLTIP } from './constant.js';

function DTIFields(_ref) {
  var className = _ref.className;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    advanced = _useState2[0],
    setAdvanced = _useState2[1];
  return /*#__PURE__*/React.createElement("div", {
    className: className
  }, /*#__PURE__*/React.createElement(Spacer, {
    marginBottom: "sm"
  }, /*#__PURE__*/React.createElement(TextField, {
    label: "Annual income",
    name: FieldConfig.annualIncome.name,
    leftAdornment: "$",
    config: FieldConfig.annualIncome.config,
    autoComplete: "off",
    format: commaSeparateNumber,
    required: true,
    tooltip: {
      title: 'Annual income',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "Your total annual earnings before taxes, for you and your co-borrower. Annual income includes your base salary and any additional commissions, bonuses, tips, and investment income."),
      interactionEventCmsInfo: {
        block_type_cd: MODAL_DIALOG,
        block_id: DTI_CALC,
        block_displayed_txt: 'Annual income'
      }
    },
    onBlur: function onBlur() {
      trackLabel('Annual Income Edit');
    },
    onChange: function onChange() {
      handleFieldChangeEvent('Annual income', 'form_field');
    }
  }), /*#__PURE__*/React.createElement(FormHelp, {
    marginTop: "xs"
  }, "Include co-borrower's salary")), /*#__PURE__*/React.createElement(TextField, {
    label: "Min. credit card payments",
    name: FieldConfig.minCreditCardPayments.name,
    leftAdornment: "$",
    rightAdornment: "/month",
    config: FieldConfig.minCreditCardPayments.config,
    autoComplete: "off",
    format: commaSeparateNumber,
    required: true,
    tooltip: {
      title: 'Minimum credit card payments',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "The lowest amount of money that you are required to pay on your credit card each month, as appears on your monthly statement. Do not include credit card balances that you pay off in full each month."),
      interactionEventCmsInfo: {
        block_type_cd: TOOLTIP,
        block_id: DTI_CALC,
        block_displayed_txt: 'Min. credit card payments'
      }
    },
    onBlur: function onBlur() {
      trackLabel('Min Credit Card Payment Edit');
    },
    onChange: function onChange() {
      handleFieldChangeEvent('Min. credit card payments', 'form_field');
    }
  }), /*#__PURE__*/React.createElement(TextField, {
    label: "Car loan payments",
    name: FieldConfig.carLoanPayments.name,
    leftAdornment: "$",
    rightAdornment: "/month",
    config: FieldConfig.carLoanPayments.config,
    autoComplete: "off",
    format: commaSeparateNumber,
    required: true,
    tooltip: {
      title: 'Car loan payments',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "The total amount you pay each month on any car loans. Enter $0 if you do not have a car loan or loan or have paid or plan to pay the loan balance off in full before applying for a mortgage."),
      interactionEventCmsInfo: {
        block_type_cd: TOOLTIP,
        block_id: DTI_CALC,
        block_displayed_txt: 'Car loan payments'
      }
    },
    onBlur: function onBlur() {
      trackLabel('Car Loan Payment Edit');
    },
    onChange: function onChange() {
      handleFieldChangeEvent('Car loan payments', 'form_field');
    }
  }), /*#__PURE__*/React.createElement(TextField, {
    label: "Student loan payments",
    name: FieldConfig.studentLoanPayments.name,
    leftAdornment: "$",
    rightAdornment: "/month",
    config: FieldConfig.studentLoanPayments.config,
    autoComplete: "off",
    format: commaSeparateNumber,
    required: true,
    tooltip: {
      title: 'Student loan payments',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "The total amount you pay each month on student loans. Enter $0 if you do not have a student loan or have paid or plan to pay the loan balance off in full before applying for a mortgage."),
      interactionEventCmsInfo: {
        block_type_cd: TOOLTIP,
        block_id: DTI_CALC,
        block_displayed_txt: 'Student loan payments'
      }
    },
    onBlur: function onBlur() {
      trackLabel('Student Loan Payment Edit');
    },
    onChange: function onChange() {
      handleFieldChangeEvent('Student loan payments', 'form_field');
    }
  }), /*#__PURE__*/React.createElement(Spacer, {
    marginBottom: "xs"
  }, /*#__PURE__*/React.createElement(TextButton, {
    type: "button",
    onClick: yieldCallback(function () {
      setAdvanced(!advanced);
      if (advanced) {
        trackLabel('Show Simple Calc');
        handleFieldChangeEvent('See less', 'toggle');
      } else {
        trackLabel('Show Advanced Calc');
        handleFieldChangeEvent('See more', 'toggle');
      }
    })
  }, advanced ? 'See less' : 'See more')), advanced && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TextField, {
    label: "Alimony/child support payments",
    name: FieldConfig.alimonySupportPayments.name,
    leftAdornment: "$",
    rightAdornment: "/month",
    config: FieldConfig.alimonySupportPayments.config,
    autoComplete: "off",
    format: commaSeparateNumber,
    required: true,
    tooltip: {
      title: 'Alimony/child support payments',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "If you make payments every month for alimony or child support, enter the total amount amount paid."),
      interactionEventCmsInfo: {
        block_type_cd: TOOLTIP,
        block_id: DTI_CALC,
        block_displayed_txt: 'Alimony/child support payments'
      }
    },
    onBlur: function onBlur() {
      trackLabel('Alimony or Child Supports Payment Edit');
    },
    onChange: function onChange() {
      handleFieldChangeEvent('Alimony/child support payments', 'form_field');
    }
  }), /*#__PURE__*/React.createElement(TextField, {
    label: "Secondary home expenses",
    name: FieldConfig.secondaryHomeExpenses.name,
    leftAdornment: "$",
    rightAdornment: "/month",
    config: FieldConfig.secondaryHomeExpenses.config,
    autoComplete: "off",
    format: commaSeparateNumber,
    required: true,
    tooltip: {
      title: 'Secondary home expenses',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "The total amount of mortgage payments or maintenance costs paid on additional vacation or rental property that you may own. Do not include your new mortgage payments, current mortgage or rent that eslint-disable-next-line react/no-unescaped-entities you'll no longer pay after buying a new home."),
      interactionEventCmsInfo: {
        block_type_cd: TOOLTIP,
        block_id: DTI_CALC,
        block_displayed_txt: 'Secondary home expenses'
      }
    },
    onBlur: function onBlur() {
      trackLabel('Secondary Home Expense Edit');
    },
    onChange: function onChange() {
      handleFieldChangeEvent('Secondary home expenses', 'form_field');
    }
  }), /*#__PURE__*/React.createElement(TextField, {
    label: "Other loan or debt payments",
    name: FieldConfig.otherDebtPayments.name,
    leftAdornment: "$",
    rightAdornment: "/month",
    config: FieldConfig.otherDebtPayments.config,
    autoComplete: "off",
    format: commaSeparateNumber,
    required: true,
    tooltip: {
      title: 'Other loan or debt payments',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "The total amount of additional debt obligations, such as personal loans. Do not include your new mortgage payments, current mortgage or rent that you'll no longer pay after buying a new home."),
      interactionEventCmsInfo: {
        block_type_cd: TOOLTIP,
        block_id: DTI_CALC,
        block_displayed_txt: 'Other loan or debt payments'
      }
    },
    onBlur: function onBlur() {
      trackLabel('Other Loan or Debt Payment Edit');
    },
    onChange: function onChange() {
      handleFieldChangeEvent('Other loan or debt payments', 'form_field');
    }
  })));
}
DTIFields.defaultProps = {
  className: undefined
};
DTIFields.propTypes = process.env.NODE_ENV !== "production" ? {
  className: PropTypes.string
} : {};

export { DTIFields as default };
